import { extractUrls, type UrlEntity } from '../domain/note'

const INLINE_MARKDOWN_URL_PATTERN = /!?\[((?:\\.|[^\]\\\n])*)\]\((https?:\/\/[^\s<>()\[\]{}"']+)(?:\s+["'][^"']*["'])?\)/gi
const AUTOLINK_URL_PATTERN = /<(https?:\/\/[^\s<>()\[\]{}"']+)>/gi
const RAW_URL_PATTERN = /https?:\/\/[^\s<>()\[\]{}"']+/gi
const INLINE_MARKDOWN_LINK_PATTERN = /!?\[((?:\\.|[^\]\\\n])*)\]\([^()\s]+(?:\s+["'][^"']*["'])?\)/gi
const REMOVED_URL_MARKER = '\uE000'
const STRUCTURALLY_EMPTY_URL_LINE = /^\s*(?:(?:#{1,6}|>|[-*+]|\d+[.)])\s+)?(?:\[[ xX]\]\s*)?\uE000[.,;:!?]*\s*$/

/**
 * Removes literal Markdown delimiters from rendered card text without changing the
 * note itself. Parsed Markdown has already consumed valid delimiters; this catches
 * incomplete or metadata-supplied fragments such as a title ending in `==`.
 */
export function hideMarkdownPreviewArtifacts(value: string): string {
  return value
    .replace(INLINE_MARKDOWN_LINK_PATTERN, '$1')
    .replace(/\\([\\!"#$%&'()*+,\-./:;<=>?@[\]^_`{|}~])/g, '$1')
    // A search-index excerpt has no line structure, so a real heading marker can
    // arrive in the middle of rendered text. Parsed headings no longer contain
    // hashes; removing only whitespace-delimited markers cannot affect hashtags.
    .replace(/(^|\s)#{1,6}[\t ]+/g, '$1')
    .replace(/(?:\*{1,3}|_{1,3}|~{2,}|`{1,3}|={2,}|-{3,})/g, '')
    .replace(/^\s{0,3}(?:[-*_]\s*){3,}$/gm, '')
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
}

function urlIdentity(value: string): string | null {
  const url = extractUrls(value)[0]
  return url ? url.canonicalUrl ?? url.originalUrl : null
}

function cleanRemovedUrlLine(line: string): string {
  if (!line.includes(REMOVED_URL_MARKER)) return line
  if (STRUCTURALLY_EMPTY_URL_LINE.test(line)) return ''

  return line
    .replace(/[ \t]*\uE000[ \t]*/g, (match, offset: number, source: string) => {
      const before = source[offset - 1] ?? ''
      const after = source[offset + match.length] ?? ''
      const separatesWords = /[\p{L}\p{N}\])}]/u.test(before) && /[\p{L}\p{N}\[({]/u.test(after)
      const followsPunctuation = /[,;:]/.test(before) && /[\p{L}\p{N}\[({]/u.test(after)
      return separatesWords || followsPunctuation ? ' ' : ''
    })
    .replace(/\(\s*\)/g, '')
    .replace(/[ \t]+([,.;!?])/g, '$1')
    .replace(/[ \t]+$/g, '')
}

/**
 * Removes all Markdown occurrences that resolve to one preview card's canonical URL. A link with a label keeps its label
 * (the words stay) unless `keepWords` is false, which removes the label with the link.
 */
export function removeUrlFromMarkdown(markdown: string, target: UrlEntity, { keepWords = true }: { keepWords?: boolean } = {}): string {
  const targetIdentity = target.canonicalUrl ?? target.originalUrl
  const matchesTarget = (value: string) => urlIdentity(value) === targetIdentity

  const withoutMarkdownTargets = markdown.replace(
    INLINE_MARKDOWN_URL_PATTERN,
    (match, label: string, value: string) => matchesTarget(value) ? (keepWords ? label : REMOVED_URL_MARKER) : match,
  )
  const withoutAutolinks = withoutMarkdownTargets.replace(
    AUTOLINK_URL_PATTERN,
    (match, value: string) => matchesTarget(value) ? REMOVED_URL_MARKER : match,
  )
  const withoutRawUrls = withoutAutolinks.replace(RAW_URL_PATTERN, (match) => {
    const trailingPunctuation = match.match(/[.,;:!?]+$/)?.[0] ?? ''
    const value = trailingPunctuation ? match.slice(0, -trailingPunctuation.length) : match
    return matchesTarget(value) ? `${REMOVED_URL_MARKER}${trailingPunctuation}` : match
  })

  return withoutRawUrls
    .split('\n')
    .map(cleanRemovedUrlLine)
    .join('\n')
    .replace(/^\n+|\n+$/g, '')
    .replace(/\n{3,}/g, '\n\n')
}

export function removeDuplicateLeadingHeading(markdown: string, title: string): string {
  const heading = markdown.match(/^\s{0,3}#{1,6}[\t ]+(.+?)[\t ]*#*[\t ]*(?:\r?\n|$)/)
  if (!heading) return markdown
  const headingText = (heading[1] ?? '').replace(/[*_~`]/g, '').trim()
  return headingText === title ? markdown.slice(heading[0].length).trimStart() : markdown
}

function normalizeDerivedTitleLine(line: string): string {
  return line
    .trim()
    .replace(/^#{1,6}\s+/, '')
    .replace(/^[-*+]\s+(?:\[[ xX]\]\s+)?/, '')
    .replace(INLINE_MARKDOWN_LINK_PATTERN, '$1')
    .replace(/<https?:\/\/[^\s<>()\[\]{}"']+>/gi, '')
    .replace(/[*_~`]/g, '')
    .replace(/^https?:\/\/\S+$/i, '')
    .trim()
}

function matchesDerivedTitle(line: string, title: string): boolean {
  const normalizedLine = normalizeDerivedTitleLine(line)
  if (!normalizedLine) return false
  if (normalizedLine === title) return true
  return title.endsWith('…') && normalizedLine.startsWith(title.slice(0, -1))
}

/** Removes the first body line that supplied an automatically derived card title. */
export function removeAutoDerivedTitleLine(markdown: string, title: string): string {
  const lines = markdown.split('\n')
  const lineIndex = lines.findIndex((line) => (
    line.trim().length > 0
    && !/^[-*+]\s+\[[ xX]\]\s*$/.test(line.trim())
  ))
  if (lineIndex < 0 || !matchesDerivedTitle(lines[lineIndex] ?? '', title)) return markdown

  lines.splice(lineIndex, 1)
  return lines.join('\n').trimStart()
}

/** Removes URL targets from dense note previews when rich cards represent those links nearby. */
export function removeUrlsForCardPreview(markdown: string): string {
  return markdown
    .replace(/^\s*\[(?:\\.|[^\]\\\n])+\]\(https?:\/\/[^\s)]+(?:\s+["'][^"']*["'])?\)[.,;:!?]*\s*$/gim, '')
    .replace(/\[((?:\\.|[^\]\\\n])+)\]\(https?:\/\/[^\s)]+(?:\s+["'][^"']*["'])?\)/gi, '$1')
    .replace(/[ \t]*<?https?:\/\/[^\s<>()\[\]{}"']+>?[ \t]*/gi, ' ')
    .replace(/[ \t]+([,.;!?])/g, '$1')
    .replace(/[ \t]+$/gm, '')
    .replace(/\n{3,}/g, '\n\n')
    .trim()
}

export function isLinkOnlyMarkdown(markdown: string, urlCount: number): boolean {
  return urlCount > 0 && removeUrlsForCardPreview(markdown).length === 0
}
